import type { ApiClient, TimelineQuery } from '../api/client.js';
import type { ArchivedContent } from '../api/types.js';
import { ApiError, NetworkError } from '../api/client.js';
import type { Item } from '../api/types.js';

/**
 * The timeline, as a store rather than as component state (M1.16.1).
 *
 * Kept out of React on purpose. Pagination, optimistic marks and their
 * reconciliation are the parts most likely to be wrong, and they are testable
 * here without rendering anything — the component is then a projection of a
 * snapshot, which is the easy half.
 *
 * ## Optimistic, then reconciled — never recomputed
 *
 * Marking an item read flips it locally at once, because the alternative is a
 * list that lags the keyboard. The **counts**, though, are never derived from
 * the items on screen: D2 makes the server authoritative, and a client that
 * tallies its own list will disagree with the badge the moment an item is
 * outside the loaded page. So the mutation's response carries the counts and
 * they replace what was held.
 *
 * If the request fails, the local flip is undone. A read state that silently
 * disagrees with the server is the one bug in this product that destroys trust
 * outright, because the user cannot see it happening.
 *
 * ## `hasMore` is the server's, not an inference
 *
 * A page can come back short, or empty, while more remains — the horizon in
 * `shared/src/timeline/assemble.ts` explains why. So "we got fewer than we
 * asked for" is not the end of the list, and the loop continues until the
 * server says `hasMore: false`.
 */

export interface TimelineScope {
  readonly filter: 'unread' | 'all';
  readonly folderId?: string | undefined;
  readonly feedId?: string | undefined;
}

export type TimelineFailure =
  | { readonly kind: 'offline'; readonly message: string }
  | { readonly kind: 'api'; readonly message: string; readonly correlationId?: string };

export interface TimelineSnapshot {
  readonly items: readonly Item[];
  readonly scope: TimelineScope;
  readonly loading: boolean;
  readonly hasMore: boolean;
  readonly reachedEnd: boolean;
  readonly error: TimelineFailure | undefined;
  readonly selectedId: string | undefined;
  /** Items marked read in this session, for the triage instrumentation. */
  readonly markedInSession: number;
}

export const PAGE_SIZE = 50;

/** How many pages one `loadMore` will chase before yielding to the caller. */
const MAX_EMPTY_PAGES = 5;

export interface TimelineCallbacks {
  /** Counts arriving from a mutation response, which are authoritative. */
  readonly onCounts?: (counts: {
    total: number;
    perSubscription: Map<string, number>;
  }) => void;
  readonly onReachedEnd?: (markedInSession: number) => void;
}

export class TimelineStore {
  readonly #api: ApiClient;
  readonly #callbacks: TimelineCallbacks;
  #listeners = new Set<() => void>();
  #cursor: string | null = null;
  #requestId = 0;

  #snapshot: TimelineSnapshot = {
    items: [],
    scope: { filter: 'unread' },
    loading: false,
    hasMore: true,
    reachedEnd: false,
    error: undefined,
    selectedId: undefined,
    markedInSession: 0,
  };

  constructor(api: ApiClient, callbacks: TimelineCallbacks = {}) {
    this.#api = api;
    this.#callbacks = callbacks;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  };

  getSnapshot = (): TimelineSnapshot => this.#snapshot;

  /**
   * Starts again with a new scope — a folder click, or a filter change.
   *
   * **Invalidates any load in flight**, and that took a test to notice. The
   * first version left the request counter alone, so a scope change during a
   * load hit two problems at once: `loadMore` saw `loading: true` and returned
   * without fetching anything, and the old request still matched the current
   * id, so when it resolved it filled the new scope with the old scope's
   * items. Somebody clicking between folders faster than the network would see
   * the wrong feed's articles under the right feed's name.
   */
  async setScope(scope: TimelineScope): Promise<void> {
    this.#cursor = null;
    // Any response still in flight now belongs to a list that no longer
    // exists, and `loading` was its flag, not this one's.
    this.#requestId += 1;
    this.#patch({
      scope,
      items: [],
      hasMore: true,
      reachedEnd: false,
      loading: false,
      error: undefined,
      selectedId: undefined,
    });
    await this.loadMore();
  }

  /**
   * Fetches the next page, or several if they come back empty.
   *
   * The loop is the practical consequence of the horizon: under `unread` a
   * page can legitimately contain nothing while the list goes on, and a
   * scroller that stopped there would look like the end. Bounded, so a very
   * long run of read items yields control rather than blocking the UI.
   */
  async loadMore(): Promise<void> {
    if (this.#snapshot.loading || !this.#snapshot.hasMore) {
      return;
    }

    const request = (this.#requestId += 1);
    this.#patch({ loading: true, error: undefined });

    try {
      let added = 0;
      let pages = 0;

      while (pages < MAX_EMPTY_PAGES) {
        pages += 1;

        const query: TimelineQuery = {
          filter: this.#snapshot.scope.filter,
          limit: PAGE_SIZE,
          ...(this.#snapshot.scope.folderId === undefined
            ? {}
            : { folderId: this.#snapshot.scope.folderId }),
          ...(this.#snapshot.scope.feedId === undefined
            ? {}
            : { feedId: this.#snapshot.scope.feedId }),
          ...(this.#cursor === null ? {} : { cursor: this.#cursor }),
        };

        const page = await this.#api.timeline(query);

        // A scope change or a second load started while this was in flight;
        // its results belong to a list that no longer exists.
        if (request !== this.#requestId) {
          return;
        }

        /*
         * The cursor was refused and the server restarted from the top, so
         * what we hold was built against a cursor it no longer honours.
         * Replacing rather than appending is the only correct move.
         */
        const items = page.restarted
          ? page.items
          : [...this.#snapshot.items, ...page.items];

        this.#cursor = page.nextCursor;
        added += page.items.length;

        const reachedEnd = !page.hasMore;

        /*
         * **Nothing is auto-selected**, and that is a decision rather than an
         * omission. Selecting an item opens it in the reader pane, and opening
         * an item marks it read (M1.16.2) — so auto-selecting the newest item
         * would mark it read every time somebody loaded the application,
         * without them having read a word. Found by a component test, which
         * is the only place the two behaviours meet.
         */
        this.#patch({
          items: dedupe(items),
          hasMore: page.hasMore,
          reachedEnd: this.#snapshot.reachedEnd || reachedEnd,
        });

        if (reachedEnd) {
          this.#callbacks.onReachedEnd?.(this.#snapshot.markedInSession);
          break;
        }

        if (added > 0) {
          break;
        }
      }
    } catch (error) {
      if (request === this.#requestId) {
        this.#patch({ error: describe(error) });
      }
    } finally {
      if (request === this.#requestId) {
        this.#patch({ loading: false });
      }
    }
  }

  select(itemId: string | undefined): void {
    this.#patch({ selectedId: itemId });
  }

  /** `j` and `k`: the next or previous item, staying inside the list. */
  move(delta: 1 | -1): Item | undefined {
    const { items, selectedId } = this.#snapshot;
    if (items.length === 0) {
      return undefined;
    }

    const current = items.findIndex((item) => item.id === selectedId);
    const next =
      current === -1 ? 0 : Math.min(items.length - 1, Math.max(0, current + delta));
    const target = items[next];

    if (target !== undefined) {
      this.#patch({ selectedId: target.id });
    }

    /*
     * Near the end, fetch ahead. A reader moving with `j` should not meet a
     * spinner at the bottom of a page they are already looking at.
     */
    if (next >= items.length - 5) {
      void this.loadMore();
    }

    return target;
  }

  /**
   * Marks one item read or unread, optimistically.
   *
   * The item stays in the list even under `filter=unread`: removing it as the
   * user marks it would move everything below up by one line, which is
   * unusable with a keyboard. It is filtered out on the next load instead.
   */
  /**
   * The signed URL for an item's archived body (M3.8.1).
   *
   * Passed straight through rather than cached. The URL is short-lived by
   * design, so a cache would hand a reader an expired one the second time
   * they opened the same article — and the gate's own check is the thing
   * that must run on every open anyway.
   */
  async archivedContent(itemId: string): Promise<ArchivedContent> {
    return this.#api.archivedContent(itemId);
  }

  async setRead(itemId: string, read: boolean): Promise<void> {
    const before = this.#snapshot.items;
    const target = before.find((item) => item.id === itemId);

    if (target === undefined || target.read === read) {
      return;
    }

    this.#patch({
      items: before.map((item) => (item.id === itemId ? { ...item, read } : item)),
      markedInSession: this.#snapshot.markedInSession + (read ? 1 : 0),
    });

    try {
      const result = await this.#api.markRead(itemId, read);

      this.#callbacks.onCounts?.({
        total: result.total,
        perSubscription: new Map(
          result.updatedCounts.map((entry) => [entry.subscriptionId, entry.unreadCount]),
        ),
      });
    } catch (error) {
      /*
       * Put it back. A client that quietly shows an item as read when the
       * server disagrees is the one failure in this product that a user
       * cannot see happening, and it destroys the thing the product sells.
       */
      this.#patch({
        items: this.#snapshot.items.map((item) =>
          item.id === itemId ? { ...item, read: target.read } : item,
        ),
        markedInSession: Math.max(0, this.#snapshot.markedInSession - (read ? 1 : 0)),
        error: describe(error),
      });
    }
  }

  /** `shift+a`. The whole scope, in one request. */
  async markAllRead(): Promise<void> {
    const { scope } = this.#snapshot;

    const target =
      scope.feedId !== undefined
        ? { subscriptionId: scope.feedId }
        : scope.folderId !== undefined
          ? { folderId: scope.folderId }
          : undefined;

    if (target === undefined) {
      /*
       * No "mark everything read" across the whole account from a keystroke.
       * It is the one destructive action here, `shift+a` is two keys from
       * `a`, and there is no undo — so it needs a scope the user chose.
       */
      return;
    }

    const before = this.#snapshot.items;
    this.#patch({ items: before.map((item) => ({ ...item, read: true })) });

    try {
      const result = await this.#api.markAllRead(target, crypto.randomUUID());
      this.#callbacks.onCounts?.({
        total: result.total,
        perSubscription: new Map(
          result.updatedCounts.map((entry) => [entry.subscriptionId, entry.unreadCount]),
        ),
      });
    } catch (error) {
      this.#patch({ items: before, error: describe(error) });
    }
  }

  #patch(partial: Partial<TimelineSnapshot>): void {
    this.#snapshot = { ...this.#snapshot, ...partial };
    for (const listener of this.#listeners) {
      listener();
    }
  }
}

/**
 * The same item can arrive twice when a page is restarted mid-scroll.
 *
 * Cheap insurance rather than a claim about the server: the property test in
 * `shared/src/timeline` says a clean pagination never repeats, and this covers
 * the case where two paginations were interleaved by a scope change.
 */
function dedupe(items: readonly Item[]): Item[] {
  const seen = new Set<string>();
  const unique: Item[] = [];

  for (const item of items) {
    if (seen.has(item.id)) {
      continue;
    }
    seen.add(item.id);
    unique.push(item);
  }

  return unique;
}

function describe(error: unknown): TimelineFailure {
  if (error instanceof NetworkError) {
    return {
      kind: 'offline',
      message: 'The service could not be reached.',
    };
  }

  if (error instanceof ApiError) {
    return {
      kind: 'api',
      message: error.userMessage,
      ...(error.correlationId === undefined
        ? {}
        : { correlationId: error.correlationId }),
    };
  }

  return { kind: 'api', message: 'Something went wrong.' };
}
